
/**
 * Module dependencies.
 */

import { FieldAlert } from '../../forms/field-alert';
import { FieldError } from '../../forms/field-error';
import { FormControl, FormControlWrapper, FormGroup, Label } from '../../forms/fields-styled-components';
import { IMaskInput } from 'react-imask';
import { ReactNode, useCallback } from 'react';
import { Text } from '../../typography';
import { color, media } from 'react-components/styles';
import { ifProp, theme } from 'styled-tools';
import { isEmpty, isNil } from 'lodash';
import { useField, useFormState } from '@seegno/react-forms';
import { useTranslate } from '../../../i18n';
import styled, { css } from 'styled-components';

/**
 * Export `NumberInputProps` type.
 */

export type NumberInputProps = {
  'aria-describedby'?: string;
  autoFocus?: boolean;
  children?: ReactNode;
  className?: string;
  decimalPlaces?: number;
  disabled?: boolean;
  helpText?: ReactNode;
  id?: string;
  label: string;
  max?: number;
  min?: number;
  name: string;
  symbol?: string | null | undefined;
  thousandsSeparator?: string;
  valueType: 'number' | 'string';
};

/**
 * `StyledFormControlWrapper` styled component.
 */

const StyledFormControlWrapper = styled(FormControlWrapper)<{ hasChildren: boolean }>`
  ${ifProp('hasChildren', css`
    align-items: center;
    column-gap: 4px;
    display: grid;
    grid-template-columns: max-content auto max-content;
  `)}
`;

/**
 * `Symbol` styled component.
 */

const Symbol = styled(Text).attrs({ as: 'span', size: 'normal' })<{ disabled: boolean }>`
  color: ${color('gray500')};
  padding: 20px 0 6px 10px;
  pointer-events: none;
  transition: color ${theme('animations.defaultTransition')};

  ${ifProp('disabled', css`
    color: ${color('gray400')};
  `)}

  ${media.min('md')`
    padding-left: 14px;
    padding-top: 22px;
  `}
`;

/**
 * `StyledFormControl` styled component.
 */

const StyledFormControl = styled(FormControl).withConfig({
  shouldForwardProp: prop => !['hasSymbol'].includes(prop)
})<{ hasSymbol: boolean }>`
  ${ifProp('hasSymbol', css`
    padding-left: 0;
    padding-right: 8px;
  `)}
`;

/**
 * Parse number.
 */

function parseNumber(value: string | null | undefined): number | null | undefined {
  if (!value) {
    return null;
  }

  return parseFloat(value);
}

/**
 * Parse value.
 */

function parseValue(
  value: string | null | undefined,
  valueType: 'number' | 'string'
): number | string | null | undefined {
  switch (valueType) {
    case 'number':
      return parseNumber(value);

    default:
      return isEmpty(value) ? undefined : value;
  }
}

/**
 * Get decimal separator by locale.
 */

function getDecimalSeparatorByLocale(locale: string): string {
  const formatter = new Intl.NumberFormat(locale);

  return formatter.formatToParts(0.66)?.find(({ type }) => type === 'decimal')?.value ?? '.';
}

/**
 * Export `NumberInput` component.
 */

export function NumberInput(props: NumberInputProps): JSX.Element {
  const {
    children,
    className,
    decimalPlaces = 0,
    disabled,
    helpText,
    id,
    label,
    name,
    symbol,
    valueType = 'string',
    ...rest
  } = props;

  const { error, meta, onChange, value, ...fieldProps } = useField(name);
  const { active } = meta || {};
  const { alreadySubmitted, isSubmitting } = useFormState();
  const { i18n } = useTranslate();
  const locale = i18n.language;
  const hasError = !!error && alreadySubmitted;
  const isDisabled = disabled || isSubmitting;
  const isFloatingLabel = Boolean(value) || active || value === 0;
  const handleChange = useCallback((value: any) => {
    onChange(parseValue(value, valueType));
  }, [onChange, valueType]);

  return (
    <FormGroup
      className={className}
      disabled={isDisabled}
      hasAlert={hasError || !!helpText}
    >
      <StyledFormControlWrapper
        disabled={isDisabled}
        hasChildren={!!children}
        hasError={hasError}
        hasFocus={active}
      >
        <Label
          disabled={isDisabled}
          htmlFor={id ?? name}
          isFloating={symbol ?? isFloatingLabel}
        >
          {label}
        </Label>

        {symbol && (
          <Symbol disabled={isDisabled}>
            {symbol}
          </Symbol>
        )}

        <StyledFormControl
          {...rest}
          {...fieldProps}
          as={IMaskInput}
          disabled={isDisabled}
          hasSymbol={symbol}
          id={id ?? name}
          inputMode={'decimal'}
          mask={Number}
          name={name}
          onAccept={handleChange}
          radix={getDecimalSeparatorByLocale(locale)}
          scale={decimalPlaces}
          unmask
          value={isNil(value) ? '' : value.toString()}
        />

        {children}
      </StyledFormControlWrapper>

      {helpText && (
        <FieldAlert isOpen={!hasError}>
          {helpText}
        </FieldAlert>
      )}

      <FieldError
        error={error}
        isOpen={hasError}
        name={name}
      />
    </FormGroup>
  );
}
